import toast from 'react-hot-toast';
import type { useQueries, useQuery } from '@tanstack/react-query';
import { create } from 'zustand';
import { z } from 'zod';

import type { TAccountProductType } from '../components/types';
import { APIEndPoints, Request } from '../apis';
import type { TAccountProduct } from '../common';
import type { AccountStoreState } from './AccountContext';
import { MINUTE } from '../common/utils/constants';

type AvailableTypes = ReadonlyArray<TAccountProductType>;
type ProductStoreState = {
  availableTypes: AvailableTypes;
  availableTypesLoading: boolean;
  // Mutations
  setAvailableTypes: (types: AvailableTypes) => void;
  setAvailableTypesLoading: (isLoading: boolean) => void;
  clearState: () => void;
};

const STARTING_STATE = {
  availableTypes: [],
  availableTypesLoading: true
} as const;

export const useProductStore = create<ProductStoreState>((set) => ({
  ...STARTING_STATE,
  setAvailableTypes: (availableTypes) => {
    set({
      availableTypes
    });
  },
  setAvailableTypesLoading: (isLoading) => {
    set({
      availableTypesLoading: isLoading
    });
  },
  clearState: () => {
    set({ ...STARTING_STATE });
  }
}));

export const clearProductStore = () => {
  useProductStore.setState({ ...STARTING_STATE });
};

export const queryAvailableTypes = ({
  setAvailableTypes,
  setAvailableTypesLoading,
  merchantId,
  query
}: {
  setAvailableTypes: ProductStoreState['setAvailableTypes'];
  setAvailableTypesLoading: (isLoading: boolean) => void;
  merchantId: string;
  query: typeof useQuery;
}) =>
  query({
    queryKey: [APIEndPoints.products, 'types'],
    staleTime: 30 * 60 * 1000, // 30 minutes, these don't change often
    queryFn: async () => {
      try {
        setAvailableTypesLoading(true);
        return await Request.get({
          path: `${APIEndPoints.products}/types?access_token`,
          schema: z.any()
        });
      } finally {
        setAvailableTypesLoading(false);
      }
    },
    enabled: !!merchantId,
    onSuccess: (types) => {
      setAvailableTypes(types);
    },
    onError: (error) => {
      console.error('Failed to load available product types', error);
      toast.error(
        'Something went wrong while trying to load this page. Please refresh and try again.'
      );
    }
  });

export const getProductQueries = ({
  queries,
  currentProductIds,
  addCurrentProduct,
  merchantId
}: {
  queries: typeof useQueries;
  currentProductIds: ReadonlyArray<string>;
  addCurrentProduct: AccountStoreState['addCurrentProduct'];
  merchantId: string;
}) =>
  queries({
    queries: currentProductIds.map((productId) => ({
      queryKey: ['merchant', merchantId, 'availableProducts', productId],
      queryFn: () =>
        Request.get<TAccountProduct>({
          path: `${APIEndPoints.products}/merchant/${merchantId}/${productId}?access_token&loadFees=true`,
          schema: z.any()
        }),
      onSuccess: (product: TAccountProduct) => {
        addCurrentProduct(product);
      },
      refetchOnMount: 'always' as const,
      staleTime: 30 * MINUTE
    }))
  });
